import React, { useEffect, useMemo, useState } from "react";
import { faGhost } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Badge,
  Box,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";

import { useOrg } from "@hooks";

import { fetchDemoCategorySummaries } from "@pages/overview/api";
import { useCategorySummaries } from "@pages/overview/hooks";
import { CategorySummary, WidgetInterface } from "@pages/overview/types";

const CATEGORIES_TO_DISPLAY: { [key: string]: string } = {
  code_injection: "Code injection",
  cookie_flag_security: "Cookie flag",
  csrf: "Cross-site request forgery (CSRF)",
  active_debug_code: "Active debug code",
  cryptography: "Cryptography",
  deserialization: "Deserialization",
  path_traversal: "Path traversal",
  regex: "Regex",
  open_redirect: "Open redirect",
  command_injection: "Command injection",
};

type CategorySummaryListItem = CategorySummary & {
  title: string;
};

export const OverviewCategoriesWidget: React.FC<WidgetInterface> = ({
  reposFilter,
  hasProjects,
}) => {
  /* Thinking about eventually moving all/most of the state management stuff here
     and in the useEffect into a Higher Order Component to standardize nice functionality
     like loading states.
  */
  const org = useOrg();
  const theme = useMantineTheme();
  const { data: loadedCategorySummaries } = useCategorySummaries(reposFilter);
  const [error, setError] = useState(false);
  const [demoCategorySummaries, setDemoCategorySummaries] = useState<
    CategorySummary[]
  >([]);

  const categorySummaries = useMemo(
    () =>
      hasProjects ? loadedCategorySummaries?.categories : demoCategorySummaries,
    [loadedCategorySummaries, demoCategorySummaries, hasProjects]
  );

  useEffect(() => {
    if (hasProjects || !org) return;
    fetchDemoCategorySummaries(org.id)
      .then((resp) => setDemoCategorySummaries(resp.categories))
      .catch(() => setError(true));
  }, [org, hasProjects]);

  // sort by issue count descending
  const sortedCategorySummaryItems: CategorySummaryListItem[] | undefined =
    useMemo(() => {
      if (!categorySummaries) return undefined;
      const sorted = categorySummaries
        .filter((item) =>
          Object.keys(CATEGORIES_TO_DISPLAY).includes(item.category)
        )
        .sort((a, b) => b.open_findings_count - a.open_findings_count);
      return sorted.map((item) => ({
        ...item,
        title: CATEGORIES_TO_DISPLAY[item.category],
      }));
    }, [categorySummaries]);

  return (
    <>
      {sortedCategorySummaryItems === undefined ? (
        <Stack spacing="sm" p="md">
          <Skeleton height={16} radius="xl" width="70%" />
          <Skeleton height={16} radius="xl" />
          <Skeleton height={16} radius="xl" width="80%" />
          <Skeleton height={16} radius="xl" width="60%" />
          <Skeleton height={16} radius="xl" />
          <Skeleton height={16} radius="xl" width="40%" />
        </Stack>
      ) : error ? (
        <Text color="red.8">Failed to load data</Text>
      ) : sortedCategorySummaryItems.length === 0 ? (
        <Stack
          p="lg"
          sx={{ color: theme.colors["gray"][6], height: "100%" }}
          align="center"
          justify="center"
        >
          <FontAwesomeIcon
            icon={faGhost}
            size="3x"
            color={theme.colors["blue"][3]}
          />
          <Text align="center" size="sm">
            <Title order={4} weight="normal">
              No categories found
            </Title>
            Try adjusting your filters
          </Text>
        </Stack>
      ) : (
        <div data-testid="categories-list">
          <Stack spacing="xs">
            {sortedCategorySummaryItems.map((item, idx) => (
              <Group key={item.title}>
                <Badge key={`${idx}-badge`} color="gray">
                  {item.open_findings_count}
                </Badge>
                <Box sx={{ flexGrow: 1 }} key={`${idx}-text`}>
                  {item.title}
                </Box>
              </Group>
            ))}
          </Stack>
        </div>
      )}
    </>
  );
};
